'use client'

import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { verifyAndUpdateCache, updateCacheClearDate } from '@/utils/pwaCacheManager'
import { preloadStudyData } from '@/utils/pwaPreloadManager'

/**
 * PWA Service Worker 條件式註冊組件
 * 
 * ⚠️ 已完全禁用 PWA（2025/01/02）
 * 
 * 禁用原因：
 * 1. 錯誤頻繁：在非學習頁面（/subscribe, /points, /payment）觸發 401 錯誤
 * 2. 404 錯誤：app-build-manifest.json 不存在（Next.js 15 不再生成）
 * 3. 使用範圍小：只在約 15 個學習頁面有用（約 7% 的總頁面）
 * 4. 維護成本高：需要持續處理 Service Worker、緩存策略等問題
 * 5. 價值有限：現代網路環境下，離線需求不強，API 請求通常很快（< 500ms）
 * 6. 用戶體驗：錯誤訊息可能造成困擾，預加載失敗影響體驗
 * 
 * 如果未來需要重新啟用：
 * 1. 取消註釋下面的 return null
 * 2. 修復 401 錯誤（確保只在學習頁面啟用）
 * 3. 修復 app-build-manifest.json 404 錯誤
 * 4. 測試所有學習頁面的 PWA 功能
 * 5. 考慮只在必要頁面（/study/n1~n5/vocabulary, /study/n1~n5/kanji）啟用
 * 
 * 替代方案：
 * - 使用瀏覽器原生 HTTP Cache
 * - 使用 IndexedDB 手動緩存學習資料
 * - 只在特定學習頁面實現簡單的緩存機制
 */
export default function PWARegister() {
  // ✅ 完全禁用 PWA
  // 如需重新啟用，請先閱讀上方的禁用原因和注意事項
  return null

  /* 
   * 以下代碼已禁用，保留作為參考
   * 如需重新啟用，請取消註釋上面的 return null 並恢復以下代碼
   */
  /*
  const [isRegistered, setIsRegistered] = useState(false)

  // 檢測是否為移動設備（手機/平板），排除電腦
  const checkIsMobileDevice = () => {
    const userAgent = navigator.userAgent.toLowerCase()
    
    // 明確排除桌面作業系統
    const isDesktopOS = userAgent.includes('windows') || 
                       userAgent.includes('macintosh') || 
                       (userAgent.includes('linux') && !userAgent.includes('android'))
    
    // 檢測移動設備標識
    const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i
    const isMobileUserAgent = mobileRegex.test(userAgent)
    
    // 檢測觸控設備
    const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches
    
    // 如果是桌面作業系統，直接返回 false（不提供電腦版 PWA）
    if (isDesktopOS) {
      return false
    }
    
    // 必須是移動設備 user agent 或明確的觸控設備
    return isMobileUserAgent || (hasTouchScreen && hasCoarsePointer)
  }

  useEffect(() => {
    // 只在客戶端執行
    if (typeof window === 'undefined') return
    
    // 開發模式不註冊
    if (process.env.NODE_ENV === 'development') return

    // ✅ 排除特定頁面（不需要 PWA 預加載）
    const excludedPaths = ['/points', '/subscribe', '/payment']
    if (excludedPaths.some(path => window.location.pathname === path || window.location.pathname.startsWith(`${path}/`))) {
      return
    }

    // ✅ 完全禁用 PWA（通過環境變數控制）
    if (process.env.NEXT_PUBLIC_DISABLE_PWA === 'true') {
      return
    }

    // 檢查是否為移動設備（手機/平板），電腦不提供 PWA
    if (!checkIsMobileDevice()) {
      return
    }

    // 檢查是否已經註冊過
    if (!('serviceWorker' in navigator)) {
      // 瀏覽器不支持 Service Worker
      return
    }

    const checkAndRegister = async () => {
      try {
        // 檢查用戶是否登入
        const { data: { session } } = await supabase.auth.getSession()
        
        if (!session?.user) {
          // 未登入用戶不註冊 Service Worker（只提供付費用戶）
          return
        }

        // 檢查用戶是否有有效訂閱
        try {
          const res = await fetch('/api/me/entitlement', {
            cache: 'no-store',
            credentials: 'include'
          })

          if (!res.ok) {
            // API 失敗，不註冊
            return
          }

          const data = await res.json()
          const entitlement = data.entitlement

          // 檢查是否有有效訂閱（tier 不是 'None' 且 endAt 在未來）
          const now = Date.now()
          const hasValidSubscription = 
            entitlement?.tier && 
            entitlement.tier !== 'None' && 
            entitlement?.endAt && 
            new Date(entitlement.endAt).getTime() > now

          if (!hasValidSubscription) {
            // 沒有有效訂閱，不註冊 Service Worker
            // ⚠️ 變更：不再清除已存在的緩存
            // 改為「阻擋使用，保留資料」的策略
            // 學習記錄和緩存都會保留，只阻擋新的 Service Worker 註冊
            return
          }

          // 獲取用戶等級（通過 API）
          let currentLevel = 5 // 默認 N5
          try {
            const levelRes = await fetch('/api/exam/unlock', {
              cache: 'no-store',
              credentials: 'include'
            })
            if (levelRes.ok) {
              const levelData = await levelRes.json()
              currentLevel = levelData.maxLevelUnlocked || 5
            } else if (levelRes.status === 401) {
              // 用戶未登入，使用默認值（不記錄錯誤）
              // console.log('[PWA Register] 用戶未登入，使用默認等級 N5')
            } else {
              // 其他錯誤，使用默認值
              if (process.env.NODE_ENV === 'development') {
                console.warn('[PWA Register] 獲取用戶等級失敗:', levelRes.status)
              }
            }
          } catch (error) {
            // 獲取等級失敗，使用默認值（網路錯誤等）
            if (process.env.NODE_ENV === 'development') {
              console.warn('[PWA Register] 獲取用戶等級時發生錯誤:', error)
            }
          }

          // 驗證並更新緩存清除日
          const subscriptionEndDate = new Date(entitlement.endAt).getTime()
          const { shouldClear } = await verifyAndUpdateCache(subscriptionEndDate, currentLevel)
          
          // ⚠️ 變更：不再因為 shouldClear 而清除緩存
          // verifyAndUpdateCache 現在不會返回 shouldClear: true
          // 即使訂閱已過期，也只更新狀態，不執行清除
          // 權限檢查會在 middleware 和 API routes 中處理
          if (shouldClear) {
            // 理論上不會執行到這裡（因為 verifyAndUpdateCache 不再返回 shouldClear: true）
            // 但保留此檢查以防萬一
            return
          }

          // 檢查是否已經有 Service Worker 註冊
          const registration = await navigator.serviceWorker.getRegistration()
          
          if (registration) {
            // 已經註冊過，不需要重複註冊
            // 但仍需要更新緩存清除日（已在上面完成）
            setIsRegistered(true)
            
            // 檢查是否需要預先緩存（如果還沒緩存過）
            const { getCacheMetadata } = await import('@/utils/pwaCacheManager')
            const metadata = getCacheMetadata()
            if (!metadata || metadata.preloadedCount === 0) {
              // 還沒預先緩存，現在緩存
              const levelMap: Record<number, 'n1' | 'n2' | 'n3' | 'n4' | 'n5'> = {
                1: 'n1',
                2: 'n2',
                3: 'n3',
                4: 'n4',
                5: 'n5'
              }
              const levelKey = levelMap[currentLevel] || 'n5'
              
              setTimeout(() => {
                preloadStudyData({
                  level: levelKey,
                  dataTypes: ['vocabulary', 'kanji', 'grammar'],
                  count: 200
                }).catch(() => {})
              }, 5000)
            }
            
            return
          }

          // 註冊 Service Worker
          try {
            const swRegistration = await navigator.serviceWorker.register('/sw.js', {
              scope: '/'
            })

            if (swRegistration) {
              setIsRegistered(true)
              
              // 更新緩存清除日
              updateCacheClearDate(subscriptionEndDate, currentLevel)
              
              // 預先緩存當前等級的常用內容（背景執行，不阻塞）
              const levelMap: Record<number, 'n1' | 'n2' | 'n3' | 'n4' | 'n5'> = {
                1: 'n1',
                2: 'n2',
                3: 'n3',
                4: 'n4',
                5: 'n5'
              }
              const levelKey = levelMap[currentLevel] || 'n5'
              
              // 延遲預載入，避免影響頁面載入速度
              setTimeout(() => {
                preloadStudyData({
                  level: levelKey,
                  dataTypes: ['vocabulary', 'kanji', 'grammar'],
                  count: 200 // 每種類型預先緩存前 200 個
                }).catch(() => {
                  // 預載入失敗不影響使用
                })
              }, 5000) // 5 秒後開始預載入
            }
          } catch (swError) {
            // Service Worker 註冊失敗（可能是文件不存在或其他錯誤）
            // 不顯示錯誤，靜默失敗
            // console.warn('[PWA Register] ⚠️ Service Worker 註冊失敗:', swError)
          }
        } catch (apiError) {
          // API 請求失敗，不註冊
          // console.warn('[PWA Register] ⚠️ 檢查訂閱狀態失敗:', apiError)
        }
      } catch (error) {
        // 任何錯誤都靜默處理，不影響用戶體驗
        // console.warn('[PWA Register] ⚠️ 檢查過程發生錯誤:', error)
      }
    }

    // 延遲註冊，確保頁面載入完成後再註冊
    // 避免影響首次訪問的載入速度
    const timer = setTimeout(() => {
      checkAndRegister()
    }, 2000) // 2 秒後再註冊

    return () => {
      clearTimeout(timer)
    }
  }, [])

  // 這個組件不渲染任何內容
  return null
  */
}

